import api from "@/services/api";
import type {
  BusinessReviewPanelListFilterKeywords,
  BusinessReviewPanelListRequest,
  BusinessReviewPanelListResponse,
  BusinessReviewPanelListSortOrder,
} from "@/components/BusinessReviewPanelList/types/businessReviewPanelListApi.types";
import { EMPTY_BUSINESS_REVIEW_PANEL_LIST_FILTER_KEYWORDS } from "@/components/BusinessReviewPanelList/types/businessReviewPanelListApi.types";
import { normalizeBusinessReviewPanelListResponse } from "@/components/BusinessReviewPanelList/utils/businessReviewPanelListApiTransform";

const { productServiceAPI } = api;

const BUSINESS_REVIEW_PANEL_ACTIVE_LIST_PATH = "/idea/businessReviewPanelList/active";
const BUSINESS_REVIEW_PANEL_ON_HOLD_LIST_PATH = "/idea/businessReviewPanelList/hold";
const BUSINESS_REVIEW_PANEL_ARCHIVED_LIST_PATH = "/idea/businessReviewPanelList/archived";

function normalizeStringArray(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.map((x) => String(x).trim()).filter(Boolean);
}

function normalizeVendorIdArray(v: unknown): number[] {
  if (!Array.isArray(v)) return [];
  return v.map((x) => Number(x)).filter((n) => Number.isFinite(n));
}

function normalizeFilterKeywordsForPost(
  fk: BusinessReviewPanelListFilterKeywords | undefined
): BusinessReviewPanelListFilterKeywords {
  const o = fk ?? EMPTY_BUSINESS_REVIEW_PANEL_LIST_FILTER_KEYWORDS;
  return {
    productType: normalizeStringArray(o.productType),
    businessStatus: normalizeStringArray(o.businessStatus),
    businessCategoryName: normalizeStringArray(o.businessCategoryName),
    categoryManager: normalizeStringArray(o.categoryManager),
    developer: normalizeStringArray(o.developer),
    vendor: normalizeStringArray(o.vendor),
  };
}

function buildListPostBody(payload: BusinessReviewPanelListRequest): BusinessReviewPanelListRequest {
  const sortOrder: BusinessReviewPanelListSortOrder =
    payload.sortOrder === "ASC" ? "ASC" : "DESC";

  return {
    pageNo: Math.max(0, Math.floor(Number(payload.pageNo) || 0)),
    pageSize: Math.max(1, Math.floor(Number(payload.pageSize) || 10)),
    search: typeof payload.search === "string" ? payload.search : "",
    sortBy: typeof payload.sortBy === "string" && payload.sortBy.trim() ? payload.sortBy.trim() : "id",
    sortOrder,
    filterKeywords: normalizeFilterKeywordsForPost(payload.filterKeywords),
  };
}

async function postBusinessReviewPanelList(
  path: string,
  payload: BusinessReviewPanelListRequest
): Promise<BusinessReviewPanelListResponse> {
  const body = buildListPostBody(payload);
  const resp = await productServiceAPI.post<unknown>(path, body);

  const totalHeader =
    (resp as { headers?: Record<string, string> })?.headers?.["x-total-count"] ??
    (resp as { headers?: Record<string, string> })?.headers?.["X-Total-Count"];

  return normalizeBusinessReviewPanelListResponse(resp.data, body, totalHeader);
}

export { normalizeBusinessReviewPanelListResponse } from "@/components/BusinessReviewPanelList/utils/businessReviewPanelListApiTransform";

export async function postBusinessReviewPanelActiveList(
  payload: BusinessReviewPanelListRequest
): Promise<BusinessReviewPanelListResponse> {
  return postBusinessReviewPanelList(BUSINESS_REVIEW_PANEL_ACTIVE_LIST_PATH, payload);
}

export async function postBusinessReviewPanelOnHoldList(
  payload: BusinessReviewPanelListRequest
): Promise<BusinessReviewPanelListResponse> {
  return postBusinessReviewPanelList(BUSINESS_REVIEW_PANEL_ON_HOLD_LIST_PATH, payload);
}

export async function postBusinessReviewPanelArchivedList(
  payload: BusinessReviewPanelListRequest
): Promise<BusinessReviewPanelListResponse> {
  return postBusinessReviewPanelList(BUSINESS_REVIEW_PANEL_ARCHIVED_LIST_PATH, payload);
}

const BusinessReviewPanelListService = {
  postActiveList: postBusinessReviewPanelActiveList,
  postOnHoldList: postBusinessReviewPanelOnHoldList,
  postArchivedList: postBusinessReviewPanelArchivedList,
};

export default BusinessReviewPanelListService;
